import React, { useEffect, useState } from 'react';
import { XIcon, CheckIcon } from 'lucide-react';
interface SendEmailModalProps {
  settlement: {
    id: string;
    dealerName: string;
    dealerEmail: string;
    amount: number;
    currency: string;
  };
  onClose: () => void;
  onSend: (emailData: any) => void;
}
export const SendEmailModal: React.FC<SendEmailModalProps> = ({
  settlement,
  onClose,
  onSend
}) => {
  const [recipient, setRecipient] = useState(settlement.dealerEmail);
  const [cc, setCC] = useState('');
  const [bcc, setBCC] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [template, setTemplate] = useState('standard');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState({
    invoice: true,
    receipt: true,
    ordersReport: false
  });
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  // Email validation
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  // Set default message based on template
  useEffect(() => {
    switch (template) {
      case 'standard':
        setMessage(`Dear ${settlement.dealerName},\n\nYour settlement ${settlement.id} for ${settlement.currency} ${settlement.amount.toFixed(2)} has been processed.\n\nPlease find the attached documents for your reference.\n\nRegards,\nSellUp Finance Team`);
        break;
      case 'detailed':
        setMessage(`Dear ${settlement.dealerName},\n\nWe are pleased to inform you that your settlement ${settlement.id} for ${settlement.currency} ${settlement.amount.toFixed(2)} has been processed.\n\nPlease find the detailed settlement report attached to this email. This includes a breakdown of all orders and commissions for the settlement period.\n\nIf you have any questions, please don't hesitate to contact our finance team.\n\nRegards,\nSellUp Finance Team`);
        break;
      case 'payment':
        setMessage(`Dear ${settlement.dealerName},\n\nThis is to confirm that payment for settlement ${settlement.id} amounting to ${settlement.currency} ${settlement.amount.toFixed(2)} has been initiated.\n\nThe payment should reflect in your account within 2-3 business days.\n\nPlease find the payment receipt attached for your reference.\n\nRegards,\nSellUp Finance Team`);
        break;
    }
  }, [template, settlement]);
  const handleSend = () => {
    // Validate email
    if (!validateEmail(recipient)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (cc && !validateEmail(cc)) {
      setEmailError('Please enter a valid CC email address');
      return;
    }
    if (bcc && !validateEmail(bcc)) {
      setEmailError('Please enter a valid BCC email address');
      return;
    }
    setEmailError('');
    setIsSending(true);
    // Simulate sending email
    setTimeout(() => {
      setIsSending(false);
      setSendSuccess(true);
      // Close after showing success message
      setTimeout(() => {
        onSend({
          recipient,
          cc,
          bcc,
          template,
          message,
          attachments
        });
      }, 1500);
    }, 2000);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-685">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-686">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-687">
          <h2 className="text-xl font-semibold text-gray-800" data-id="element-688">
            Send Settlement Details
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-689">
            <XIcon className="h-5 w-5" data-id="element-690" />
          </button>
        </div>
        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-8rem)]" data-id="element-691">
          {sendSuccess ? <div className="flex flex-col items-center justify-center py-8" data-id="element-692">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-693">
                <CheckIcon className="h-6 w-6 text-green-600" data-id="element-694" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-695">
                Email sent successfully
              </h3>
              <p className="text-gray-500 text-center" data-id="element-696">
                The settlement details have been sent to {recipient}
              </p>
            </div> : <>
              {/* Email Form */}
              <div className="space-y-4" data-id="element-697">
                <div data-id="element-698">
                  <label htmlFor="recipient" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-699">
                    To
                  </label>
                  <input type="email" id="recipient" value={recipient} onChange={e => setRecipient(e.target.value)} className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="recipient@example.com" data-id="element-700" />
                </div>
                <div data-id="element-701">
                  <button type="button" onClick={() => setShowCcBcc(!showCcBcc)} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-702">
                    {showCcBcc ? 'Hide CC/BCC' : 'Add CC/BCC'}
                  </button>
                  {showCcBcc && <div className="mt-2 space-y-3" data-id="element-703">
                      <div data-id="element-704">
                        <label htmlFor="cc" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-705">
                          CC
                        </label>
                        <input type="email" id="cc" value={cc} onChange={e => setCC(e.target.value)} className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="cc@example.com" data-id="element-706" />
                      </div>
                      <div data-id="element-707">
                        <label htmlFor="bcc" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-708">
                          BCC
                        </label>
                        <input type="email" id="bcc" value={bcc} onChange={e => setBCC(e.target.value)} className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="bcc@example.com" data-id="element-709" />
                      </div>
                    </div>}
                </div>
                <div data-id="element-710">
                  <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-711">
                    Email Template
                  </label>
                  <select id="template" value={template} onChange={e => setTemplate(e.target.value)} className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" data-id="element-712">
                    <option value="standard" data-id="element-713">Standard Template</option>
                    <option value="detailed" data-id="element-714">Detailed Report</option>
                    <option value="payment" data-id="element-715">Payment Confirmation</option>
                  </select>
                </div>
                <div data-id="element-716">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-717">
                    Message
                  </label>
                  <div className="border border-gray-300 rounded-md overflow-hidden" data-id="element-718">
                    <div className="bg-gray-50 border-b border-gray-200 px-3 py-1.5 flex items-center" data-id="element-719">
                      <span className="text-xs text-gray-500" data-id="element-720">
                        Settlement: {settlement.id}
                      </span>
                    </div>
                    <textarea id="message" value={message} onChange={e => setMessage(e.target.value)} rows={8} className="w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-721" />
                  </div>
                </div>
                <div data-id="element-722">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-723">
                    Attachments
                  </label>
                  <div className="space-y-2" data-id="element-724">
                    <div className="flex items-center" data-id="element-725">
                      <input type="checkbox" id="invoice" checked={attachments.invoice} onChange={e => setAttachments({
                    ...attachments,
                    invoice: e.target.checked
                  })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-726" />
                      <label htmlFor="invoice" className="ml-2 text-sm text-gray-700" data-id="element-727">
                        Settlement Invoice
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-728">
                      <input type="checkbox" id="receipt" checked={attachments.receipt} onChange={e => setAttachments({
                    ...attachments,
                    receipt: e.target.checked
                  })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-729" />
                      <label htmlFor="receipt" className="ml-2 text-sm text-gray-700" data-id="element-730">
                        Payment Receipt
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-731">
                      <input type="checkbox" id="ordersReport" checked={attachments.ordersReport} onChange={e => setAttachments({
                    ...attachments,
                    ordersReport: e.target.checked
                  })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-732" />
                      <label htmlFor="ordersReport" className="ml-2 text-sm text-gray-700" data-id="element-733">
                        Orders Report
                      </label>
                    </div>
                  </div>
                </div>
                {emailError && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md" data-id="element-734">
                    {emailError}
                  </div>}
              </div>
            </>}
        </div>
        {/* Footer */}
        {!sendSuccess && <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3" data-id="element-735">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-736">
              Cancel
            </button>
            <button onClick={handleSend} disabled={isSending} className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${isSending ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center`} data-id="element-737">
              {isSending ? <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-738">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-739"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-740"></path>
                  </svg>
                  Sending...
                </> : 'Send Email'}
            </button>
          </div>}
      </div>
    </div>;
};